import {
  TASK_SOURCE_LABELS,
  TASK_STATUS_LABELS,
  tagKompakt,
  type TaskSource,
  type TaskStatus,
} from '@netcon-work/shared'
import { useState } from 'react'
import type { trpc } from '../../lib/trpc'
import { SourceBadge } from '../ui/primitives'
import { TabellenUmschalter, useHinweis } from './hinweis'

type Aufgabe = Awaited<ReturnType<typeof trpc.analytics.wochenAufgaben.query>>[number]

/** Dieselben Quellenfarben wie in der Aktivitäts-Zeitleiste – Farbe heißt hier wie dort Herkunft. */
const QUELLE_FARBE: Record<string, string> = {
  gitlab: 'var(--color-src-gitlab)',
  netcon_ticket: 'var(--color-src-netcon)',
  work: 'var(--color-src-work)',
}

/**
 * Schraffur für abwesende Tage – wortgleich mit der Zeitleiste.
 *
 * Ein Muster und keine Füllfarbe: eine graue Fläche läse sich als „wenig", eine Schraffur als
 * „gilt nicht".
 */
const ABWESEND_MUSTER =
  'repeating-linear-gradient(135deg, var(--color-line2) 0 2px, transparent 2px 5px)'

/**
 * **Wie blass ein Balken ist, der jemand anderem gehört** – der Mit-Bearbeiter aus der Quelle
 * ([E-266](../../../../../docs/ENTSCHEIDUNGEN.md#e-266)). Die Aufgabe steht in seiner Zeile, weil
 * er in GitLab mit dransteht; gezählt wird sie beim Führenden. Blass und nicht farblos: sie ist
 * da, sie ist nur nicht seine.
 *
 * Die Deckkraft trägt die Aussage **nicht allein** – Sprechblase und Vorlesetext sagen „mit".
 */
const MIT_DECKKRAFT = 0.45

export type WochenPerson = {
  personId: string
  name: string
  /** Erledigte Aufgaben je Tag – Schlüssel ist der Tagesschlüssel. */
  erledigt: Record<string, number>
  aufgaben: Aufgabe[]
}

/**
 * **Der Wochenstreifen** (Nils, 20.08.2026).
 *
 * *„Ggf. können wir auch noch eine Kachel zusätzlich erstellen, wo man das direkt für die Woche
 * sieht – mit einem Strich ab wann Plan (also neuer als jetzt) ist und was in der Vergangenheit
 * ist? Daily ist immer Tag-/Wochenbetrachtung. Wie so ein Gantt."*
 *
 * ## Der Strich ist die Aussage
 *
 * Links davon steht, was **war** – belegte Balken und die Zahl der Erledigungen je Tag. Rechts
 * steht, was **geplant** ist, gestrichelt. Beides in derselben Zeile, damit die Frage „schafft
 * der das noch" nicht aus zwei Ansichten zusammengesetzt werden muss.
 *
 * ## Warum die Balken echt sind
 *
 * Ihr Anfang kommt aus dem Ereignis-Log (erster Wechsel nach „in Arbeit"), nicht aus
 * `work_taken_at`: von 18 laufenden Aufgaben trugen am 20.08.2026 **17** kein `work_taken_at`,
 * weil sie importiert und nie in Work gezogen wurden – aber **alle 18** einen protokollierten
 * Wechsel. Das Ende ist heute, außer es gibt einen wirksamen Termin; dann läuft der Balken
 * gestrichelt bis dorthin.
 *
 * **Es wird keine Dauer geschätzt.** Ein Balken ohne Zieldatum endet am Strich – nicht
 * „vermutlich Freitag". Was länger läuft als die Woche, läuft links (◀) oder rechts (▸) aus
 * dem Bild; abgeschnitten ist ehrlicher als gestaucht.
 *
 * ## Drei Wege zu jedem Wert
 *
 * Farbe trägt nie allein (Work-Grün erreicht gegen Weiß nur 2,82:1): jeder Balken hat eine
 * Sprechblase mit Titel, Quelle und Zeitraum, dazu eine Legende und die **Tabellensicht** oben
 * rechts. Dieselbe Regel wie in der Zeitleiste.
 */
export function WochenStreifen({
  tage,
  heute,
  personen,
  abwesendAn,
  onBalkenKlick,
  onTagKlick,
  onTagSpalteKlick,
  onWartendKlick,
  zurueck,
  vor,
  beschriftung,
}: {
  /** Die sieben Tagesschlüssel der Woche, Montag zuerst. */
  tage: string[]
  heute: string
  personen: WochenPerson[]
  abwesendAn?: (personId: string, tage: readonly string[]) => Set<string>
  onBalkenKlick: (taskId: string) => void
  /** Klick auf einen vergangenen Tag – führt ins bestehende Tages-Popup. */
  onTagKlick: (personId: string, name: string, tag: string) => void
  /**
   * Klick auf die **Spaltenüberschrift** – zeigt, was an diesem Tag bei wem dran ist. Der Weg
   * fürs Daily, auch für künftige Tage (Nils, 24.08.2026).
   */
  onTagSpalteKlick: (tag: string) => void
  /** Klick auf „n später" / „n ohne Datum" – zeigt die Aufgaben dahinter. */
  onWartendKlick: (name: string, was: string, taskIds: string[]) => void
  /** Blättern – `null` heißt: in diese Richtung gibt es nichts mehr zu zeigen. */
  zurueck: (() => void) | null
  vor: (() => void) | null
  /** „KW 34 · 17.08.–23.08." – steht zwischen den Pfeilen. */
  beschriftung: string
}) {
  const { anMark, feld } = useHinweis()
  const [tabelle, setTabelle] = useState(false)

  /**
   * **Wo der Strich steht.** Am rechten Rand des heutigen Tages: „Plan" heißt bei Nils
   * *„neuer als jetzt"*, und der heutige Tag ist noch nicht vorbei. Liegt heute außerhalb der
   * Woche – möglich, sobald die Kachel einmal eine andere Woche zeigt –, gibt es keinen Strich
   * statt eines an der falschen Stelle.
   */
  const letzterTag = tage[tage.length - 1] as string
  const heuteIndex = tage.indexOf(heute)

  /**
   * Wie viele laufende Aufgaben in dieser Woche **keinen** Balken bekommen – weil die Woche
   * ganz in der Zukunft liegt. In der laufenden und in vergangenen Wochen ist das null, dort
   * zeichnen die Balken selbst.
   */
  const laufendeOhneBalken = (p: WochenPerson) =>
    heute < (tage[0] as string) ? p.aufgaben.filter((a) => a.art === 'laeuft') : []
  const strichBei = heuteIndex >= 0 ? ((heuteIndex + 1) / tage.length) * 100 : null

  /**
   * **Eine achte Spalte rechts: was wartet, ohne auf einen Tag zu gehören.**
   *
   * Nils, 20.08.2026: *„Für die Planung ist ja immer – was ist für die Person in wartend, was
   * mit welchem Zeitfenster."* Von den wartenden Aufgaben trägt heute die große Mehrheit **kein**
   * ableitbares Datum. Sie in die Woche zu legen hieße, einen Tag zu erfinden; sie wegzulassen
   * hieße, die Zeile leer zu zeigen, obwohl Arbeit bereitliegt. Also stehen sie daneben, gezählt
   * und anklickbar – außerhalb des Kalenders, weil sie außerhalb des Kalenders sind.
   */
  const spalten = `150px repeat(${tage.length}, 1fr) 104px`

  if (personen.length === 0) {
    return <p className="py-2 text-[13px] text-ink2">Diese Woche läuft nichts und lief nichts.</p>
  }

  if (tabelle) {
    return (
      <WochenTabelle
        personen={personen}
        heute={heute}
        beschriftung={beschriftung}
        zurueck={zurueck}
        vor={vor}
        onZeileKlick={onBalkenKlick}
        umschalten={() => setTabelle((t) => !t)}
      />
    )
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink2">
          <Legende>
            <span className="inline-block h-[7px] w-5 rounded-[3px] bg-ink2 align-middle" /> läuft
          </Legende>
          <Legende>
            <span
              className="inline-block h-[7px] w-5 rounded-[3px] align-middle"
              style={{
                background:
                  'repeating-linear-gradient(90deg, var(--color-ink2) 0 3px, transparent 3px 6px)',
              }}
            />{' '}
            Plan
          </Legende>
          <Legende>
            <span
              className="inline-block h-[7px] w-5 align-middle"
              style={{ background: ABWESEND_MUSTER }}
            />{' '}
            abwesend
          </Legende>
          <Legende>
            <span
              className="inline-block h-[7px] w-5 rounded-[3px] bg-ink2 align-middle"
              style={{ opacity: MIT_DECKKRAFT }}
            />{' '}
            mit (führt jemand anders)
          </Legende>
          <Legende>
            <b className="text-ink">3</b> erledigt
          </Legende>
        </div>
        <div className="flex items-center gap-1.5">
          <Blaetter richtung="zurueck" tun={zurueck} />
          <span className="tabular text-[11.5px] font-semibold">{beschriftung}</span>
          <Blaetter richtung="vor" tun={vor} />
          <TabellenUmschalter
            offen={tabelle}
            umschalten={() => setTabelle((t) => !t)}
            diagramm="Wochenstreifen"
          />
        </div>
      </div>

      {/**
       * **Die Kopfzeile ist der Einstieg ins Daily** (Nils, 24.08.2026: *„ich müsste auch auf
       * die Datumswerte klicken können, der mir dann nach Personen gruppiert den Inhalt
       * anzeigt"*).
       *
       * Der Streifen beantwortet *wie viel und wann*; ein Daily braucht *was genau*. Die Zellen
       * je Person führten schon ins Tages-Popup (was an dem Tag **passiert** ist) – nach vorn
       * gab es keinen Weg, und für morgen gibt es im Ereignis-Log auch nichts zu holen.
       *
       * Deshalb die **Spalte** statt der Zelle: ein Daily geht einen Tag reihum, nicht eine
       * Person durch die Woche.
       */}
      <div className="mt-3 grid items-end" style={{ gridTemplateColumns: spalten }}>
        <div />
        {tage.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onTagSpalteKlick(t)}
            title={`Was ist am ${tagKompakt(t)} dran – nach Personen`}
            className={`cursor-pointer rounded-md py-[1px] text-center text-[10.5px] hover:bg-page hover:underline ${
              t === heute ? 'font-bold text-ink' : 'text-muted'
            }`}
          >
            {tagKompakt(t)}
          </button>
        ))}
        <div className="pl-2 text-right text-[10.5px] text-muted">wartet</div>
      </div>

      <div className="mt-1 space-y-1.5">
        {personen.map((p) => {
          const abwesend = abwesendAn ? abwesendAn(p.personId, tage) : new Set<string>()
          const laufend = laufendeOhneBalken(p)
          return (
            <div key={p.personId} className="grid" style={{ gridTemplateColumns: spalten }}>
              {/**
               * **Der Name steht oben, nicht mittig.**
               *
               * Mittig hieß: seine Höhe hängt davon ab, wie viele Balken die Person hat – bei
               * fünf Balken rutscht er nach unten, bei keinem bleibt er oben. Nichts lag mehr
               * auf einer Linie, und die Zahlen rechts standen neben einem anderen Namen als
               * gedacht. Oben stehen Name, Tageszellen und Zahlen auf derselben Kante.
               */}
              <div className="self-start truncate pr-2 pt-[3px] text-[12px] font-semibold">
                {p.name}
                {/**
                 * **In einer künftigen Woche hat kein Balken etwas verloren** – gearbeitete
                 * Zeit liegt nicht in der Zukunft. Was läuft, steht deshalb als Zahl hier,
                 * damit die Zeile nicht so aussieht, als habe die Person nichts zu tun.
                 */}
                {laufend.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      onWartendKlick(
                        p.name,
                        'läuft',
                        laufend.map((a) => a.taskId),
                      )
                    }
                    className="ml-1.5 cursor-pointer text-[10.5px] font-normal text-ink2 hover:underline"
                  >
                    {laufend.filter((a) => !a.mit).length} läuft
                    {laufend.some((a) => a.mit) && (
                      <span className="text-muted">
                        {' '}
                        +{laufend.filter((a) => a.mit).length} mit
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* Die sieben Tageszellen als Hintergrund: Schraffur und Erledigt-Zahl */}
              {/**
               * **Ohne Spaltenabstand.** Die Balken und der Strich sitzen in Prozent der
               * Gesamtbreite; mit Lücken zwischen den Zellen träfen sie die Tagesgrenzen um
               * ein paar Pixel daneben – und ein Gantt, dessen Strich nicht auf der Grenze
               * steht, behauptet einen anderen Tag. Getrennt wird deshalb mit Linien.
               */}
              <div
                className="relative col-span-7 grid"
                style={{ gridTemplateColumns: `repeat(${tage.length}, 1fr)` }}
              >
                {tage.map((t) => {
                  const anzahl = p.erledigt[t] ?? 0
                  const weg = abwesend.has(t)
                  return (
                    <button
                      key={t}
                      type="button"
                      disabled={anzahl === 0}
                      onClick={() => onTagKlick(p.personId, p.name, t)}
                      {...anMark(
                        <>
                          <b>
                            {p.name} · {tagKompakt(t)}
                          </b>
                          <br />
                          {weg && (
                            <>
                              abwesend
                              <br />
                            </>
                          )}
                          {anzahl > 0 ? `${anzahl} erledigt` : 'nichts erledigt'}
                        </>,
                      )}
                      className={`min-h-[26px] border-y border-r border-line2/50 text-[11px] first:rounded-l-[5px] first:border-l last:rounded-r-[5px] ${
                        anzahl > 0 ? 'cursor-pointer font-bold text-ink hover:bg-[#f3f2ef]' : ''
                      } ${t === heute ? 'bg-[#faf9f7]' : ''}`}
                      style={weg ? { background: ABWESEND_MUSTER } : undefined}
                      aria-label={`${p.name}, ${tagKompakt(t)}${weg ? ', abwesend' : ''}, ${anzahl} erledigt`}
                    >
                      {anzahl > 0 ? anzahl : ''}
                    </button>
                  )
                })}

                {/* Der Strich zwischen Vergangenheit und Plan */}
                {strichBei !== null && (
                  <div
                    className="pointer-events-none absolute inset-y-0 w-px bg-ink"
                    style={{ left: `${strichBei}%` }}
                    aria-hidden
                  />
                )}

                {/**
                 * **Erst was läuft, dann was in dieser Woche dran ist.** Die Reihenfolge ist
                 * die des Blicks: links unten der angefangene Balken, rechts die Marke am
                 * Zieltag. Wartendes **ohne** Datum steht nicht hier, sondern in der Spalte
                 * rechts neben der Woche – es gehört auf keinen Tag.
                 */}
                <div className="col-span-7 mt-0.5 space-y-[3px]">
                  {p.aufgaben
                    .filter((a) => a.art === 'laeuft' || (a.zielTag && tage.includes(a.zielTag)))
                    /**
                     * **Nach dem linken Rand sortiert.** Ungeordnet sehen gleich lange Balken
                     * wie ein Muster aus; geordnet entsteht eine Treppe, an der man abliest, was
                     * lange läuft und was gerade erst begonnen hat. Wartendes steht unten – es
                     * hat noch keinen Anfang.
                     */
                    .sort((x, y) => (x.startTag ?? '9999').localeCompare(y.startTag ?? '9999'))
                    .map((a) => (
                      <BalkenZeile
                        key={a.taskId}
                        aufgabe={a}
                        tage={tage}
                        heute={heute}
                        anMark={anMark}
                        onKlick={() => onBalkenKlick(a.taskId)}
                      />
                    ))}
                </div>
              </div>

              {(() => {
                const wartet = p.aufgaben.filter((a) => a.art === 'wartet')
                const ausserhalb = wartet.filter(
                  (a) => a.zielTag !== null && !tage.includes(a.zielTag),
                )
                const gruppen = [
                  {
                    schluessel: 'ueberfaellig',
                    label: 'überfällig',
                    stil: 'text-pool-sofort',
                    aufgaben: ausserhalb.filter((a) => (a.zielTag as string) < (tage[0] as string)),
                  },
                  {
                    schluessel: 'spaeter',
                    label: 'später',
                    stil: 'text-ink2',
                    aufgaben: ausserhalb.filter((a) => (a.zielTag as string) > letzterTag),
                  },
                  {
                    schluessel: 'ohne',
                    label: 'ohne Datum',
                    stil: 'text-muted',
                    aufgaben: wartet.filter((a) => a.zielTag === null),
                  },
                ].filter((g) => g.aufgaben.length > 0)

                return (
                  <div className="self-start pl-2 pt-[3px] text-right">
                    {gruppen.length === 0 && <span className="text-[11px] text-muted">–</span>}
                    {gruppen.map((g) => (
                      <button
                        key={g.schluessel}
                        type="button"
                        onClick={() =>
                          onWartendKlick(
                            p.name,
                            g.label,
                            g.aufgaben.map((a) => a.taskId),
                          )
                        }
                        {...anMark(
                          <>
                            <b>
                              {p.name} · {g.label}
                            </b>
                            <br />
                            {g.aufgaben
                              .map((a) => (a.zielTag ? tagKompakt(a.zielTag) : 'ohne Datum'))
                              .slice(0, 6)
                              .join(' · ')}
                            {g.aufgaben.length > 6 && ' …'}
                          </>,
                        )}
                        className={`block w-full cursor-pointer text-right text-[11px] font-semibold hover:underline ${g.stil}`}
                      >
                        {g.aufgaben.filter((a) => !a.mit).length} {g.label}
                        {/**
                         * **Fremde Arbeit steht daneben, nicht in der Zahl**
                         * ([E-266](../../../../../docs/ENTSCHEIDUNGEN.md#e-266)). Sonst behauptete
                         * die Spalte eine Last, die die Person nicht trägt – sie steht in der
                         * Quelle nur mit dran.
                         */}
                        {g.aufgaben.some((a) => a.mit) && (
                          <span className="font-normal text-muted">
                            {' '}
                            +{g.aufgaben.filter((a) => a.mit).length} mit
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )
              })()}
            </div>
          )
        })}
      </div>
      {feld}
    </>
  )
}

/* ── Die Tabellensicht ────────────────────────────────────────────────────── */

/**
 * Die beiden Lagen in der Reihenfolge, in der ein Daily gelesen wird: erst was **läuft**,
 * dann was **wartet**.
 *
 * Jede Lage bringt ihr eigenes Datum mit, nach dem sortiert wird – und das ist kein
 * Schönheitsfehler, sondern die Frage, die in der jeweiligen Lage zählt: bei laufender Arbeit
 * *„was zieht sich am längsten"* (`seit`), bei wartender *„was ist als Nächstes dran"*
 * (`dran am`). Damit niemand raten muss, steht die Sortierregel in der Gruppenüberschrift.
 */
const LAGEN = [
  { art: 'laeuft', label: 'läuft', regel: 'älteste zuerst', datum: 'startTag' },
  { art: 'wartet', label: 'wartet', regel: 'nächster Termin zuerst', datum: 'zielTag' },
] as const

type TabellenZeile = {
  aufgabe: Aufgabe
  person: string
  /** Nur in der ersten Zeile einer Person steht ihr Name – darunter wäre er Rauschen. */
  ersteDerPerson: boolean
}

/**
 * **Die Woche als Tabelle** (Nils, 25.08.2026: *„gruppiert nach Lage/Status, dann sortiert nach
 * Mitarbeiter, dann nach Datum"* – für die Ist-Sichtung).
 *
 * Der Streifen zeigt die Lage **über der Zeit**; diese Tabelle zeigt dieselben Aufgaben als
 * Liste, die man von oben nach unten durchgeht. Drei Ordnungen, in dieser Reihenfolge:
 *
 * | | |
 * |---|---|
 * | **Lage** | zwei Blöcke – `läuft`, dann `wartet`. Die alte Spalte „Lage" entfällt: sie stand in jeder Zeile und sagte dasselbe wie die Überschrift darüber |
 * | **Mitarbeiter** | innerhalb des Blocks; der Name steht einmal je Person, nicht in jeder Zeile |
 * | **Datum** | innerhalb der Person – ohne Datum zuletzt, weil „unbekannt" kein später ist |
 *
 * **Überfällig ist relativ zu heute, nicht zur gezeigten Woche.** Im Streifen ist „überfällig",
 * was vor dem Montag der Woche lag – da geht es um das Bild. Hier geht es um die Sichtung, und
 * dort zählt nur ein Stichtag: heute.
 */
function WochenTabelle({
  personen,
  heute,
  beschriftung,
  zurueck,
  vor,
  onZeileKlick,
  umschalten,
}: {
  personen: WochenPerson[]
  heute: string
  beschriftung: string
  zurueck: (() => void) | null
  vor: (() => void) | null
  onZeileKlick: (taskId: string) => void
  umschalten: () => void
}) {
  const ueberfaellig = (a: Aufgabe) => a.art === 'wartet' && a.zielTag !== null && a.zielTag < heute

  /**
   * Ein Block: alle Aufgaben einer Lage, nach Person und dann nach Datum. Der Titel entscheidet
   * bei gleichem Datum – sonst springen zwei Zeilen bei jedem Neuladen umeinander.
   */
  const block = (lage: (typeof LAGEN)[number]): TabellenZeile[] => {
    const zeilen = personen.flatMap((p) =>
      p.aufgaben
        .filter((a) => a.art === lage.art)
        .map((a) => ({ aufgabe: a, person: p.name, ersteDerPerson: false })),
    )
    zeilen.sort(
      (x, y) =>
        x.person.localeCompare(y.person, 'de') ||
        /** `9999` sortiert „ohne Datum" ans Ende, ohne dass es einen Sonderzweig braucht. */
        (x.aufgabe[lage.datum] ?? '9999').localeCompare(y.aufgabe[lage.datum] ?? '9999') ||
        x.aufgabe.titel.localeCompare(y.aufgabe.titel, 'de'),
    )
    return zeilen.map((z, i) => ({
      ...z,
      ersteDerPerson: i === 0 || zeilen[i - 1]?.person !== z.person,
    }))
  }

  /**
   * **Zwei Zahlen je Block: eigene und „mit"** ([E-266](../../../../../docs/ENTSCHEIDUNGEN.md#e-266)).
   *
   * Die Zeilen sind mehr als die Aufgaben, seit eine Aufgabe bei jedem ihrer Bearbeiter steht.
   * Gezählt wird weiter beim Führenden – sonst wäre die Summe größer als der Bestand. Damit die
   * Zahl nicht ihrer eigenen Liste widerspricht, steht die zweite daneben und benennt den
   * Unterschied.
   */
  const bloecke = LAGEN.map((lage) => {
    const zeilen = block(lage)
    return {
      lage,
      zeilen,
      eigene: zeilen.filter((z) => !z.aufgabe.mit).length,
      mit: zeilen.filter((z) => z.aufgabe.mit).length,
    }
  }).filter((b) => b.zeilen.length > 0)
  const anzahlUeberfaellig = bloecke
    .flatMap((b) => b.zeilen)
    .filter((z) => !z.aufgabe.mit && ueberfaellig(z.aufgabe)).length

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5 text-[11px] text-ink2">
          {bloecke.map((b) => (
            <span key={b.lage.art}>
              <b className="text-ink">{b.eigene}</b> {b.lage.label}
              {b.mit > 0 && <span className="text-muted"> · {b.mit} mit</span>}
            </span>
          ))}
          {anzahlUeberfaellig > 0 && (
            <span className="font-semibold text-pool-sofort">{anzahlUeberfaellig} überfällig</span>
          )}
        </div>
        {/**
         * **Blättern bleibt auch in der Tabelle.** Vorher stand hier nur der Umschalter – wer
         * umschaltete, verlor die Woche aus dem Blick und konnte nicht weiter. Eine Tabelle
         * ohne Angabe, welche Woche sie zeigt, ist eine Liste ohne Bezug.
         */}
        <div className="flex shrink-0 items-center gap-1.5">
          <Blaetter richtung="zurueck" tun={zurueck} />
          <span className="tabular text-[11.5px] font-semibold">{beschriftung}</span>
          <Blaetter richtung="vor" tun={vor} />
          <TabellenUmschalter offen umschalten={umschalten} diagramm="Wochenstreifen" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[640px] mt-2.5 w-full border-collapse text-xs">
          <caption className="sr-only">
            Die Aufgaben der Woche, gruppiert nach Lage, darin nach Person und Datum
          </caption>
          <thead>
            <tr>
              {[
                { k: 'Person', rechts: false },
                { k: 'Aufgabe', rechts: false },
                { k: 'Status', rechts: false },
                { k: 'Quelle', rechts: false },
                { k: 'seit', rechts: true },
                { k: 'dran am', rechts: true },
              ].map(({ k, rechts }) => (
                <th
                  key={k}
                  scope="col"
                  className={`border-b border-line px-2 py-[5px] text-[10.5px] font-semibold text-ink2 ${
                    rechts ? 'text-right' : 'text-left'
                  }`}
                >
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          {bloecke.map((b) => (
            <tbody key={b.lage.art}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={6}
                  className="border-b border-line2 bg-page px-2 py-[5px] text-left"
                >
                  <span className="text-[11.5px] font-bold">{b.lage.label}</span>
                  <span className="ml-1.5 text-[11px] font-normal text-ink2">
                    {b.eigene} {b.eigene === 1 ? 'Aufgabe' : 'Aufgaben'}
                  </span>
                  {b.mit > 0 && (
                    <span className="ml-1.5 text-[11px] font-normal text-muted italic">
                      + {b.mit} als Mit-Bearbeiter
                    </span>
                  )}
                  <span className="ml-1.5 text-[10.5px] font-normal text-muted">
                    · nach Person, {b.lage.regel}
                  </span>
                </th>
              </tr>
              {b.zeilen.map(({ aufgabe: a, person, ersteDerPerson }) => {
                /** Die Trennlinie zwischen zwei Personen – innerhalb einer Person keine. */
                const trenner = ersteDerPerson ? 'border-t border-line2' : ''
                return (
                  /**
                   * **Person **und** Aufgabe im Schlüssel:** dieselbe Aufgabe steht seit
                   * [E-266](../../../../../docs/ENTSCHEIDUNGEN.md#e-266) in mehreren Zeilen, einmal
                   * je Bearbeiter. Die Kennung allein wäre doppelt, und React zeichnete die
                   * zweite Zeile nicht.
                   */
                  <tr key={`${person}·${a.taskId}`} className="hover:bg-[#faf9f7]">
                    <td
                      className={`border-b border-line px-2 py-1.5 align-top font-semibold whitespace-nowrap ${trenner}`}
                    >
                      {ersteDerPerson ? person : ''}
                    </td>
                    <td className={`border-b border-line px-2 py-1.5 align-top ${trenner}`}>
                      {/**
                       * Der Titel führt in die Aufgabe – derselbe Weg wie über den Balken im
                       * Streifen. Eine Sichtung, aus der man nicht in die Aufgabe kommt, endet
                       * bei jeder Auffälligkeit in der Suche.
                       */}
                      <button
                        type="button"
                        onClick={() => onZeileKlick(a.taskId)}
                        className="cursor-pointer text-left hover:underline"
                      >
                        {a.titel}
                      </button>
                      {/**
                       * **Die Marke, ohne die die Liste ihrer eigenen Zahl widerspräche.** Die
                       * Aufgabe steht hier, weil die Person in der Quelle mit dransteht – geführt
                       * wird sie von jemand anderem, und dort ist sie auch gezählt.
                       */}
                      {a.mit && (
                        <span
                          className="ml-1.5 text-[10.5px] text-muted italic"
                          title="Mit-Bearbeiter aus der Quelle – geführt wird die Aufgabe von jemand anderem"
                        >
                          mit
                        </span>
                      )}
                    </td>
                    <td
                      className={`border-b border-line px-2 py-1.5 align-top text-ink2 whitespace-nowrap ${trenner}`}
                    >
                      {TASK_STATUS_LABELS[a.status as TaskStatus] ?? a.status}
                    </td>
                    <td className={`border-b border-line px-2 py-1.5 align-top ${trenner}`}>
                      <SourceBadge source={a.quelle as TaskSource} />
                    </td>
                    <td
                      className={`tabular border-b border-line px-2 py-1.5 text-right align-top whitespace-nowrap ${trenner} ${
                        a.startTag ? 'text-ink2' : 'text-muted'
                      }`}
                    >
                      {a.startTag ? tagKompakt(a.startTag) : '–'}
                    </td>
                    <td
                      className={`tabular border-b border-line px-2 py-1.5 text-right align-top whitespace-nowrap ${trenner} ${
                        ueberfaellig(a)
                          ? 'font-semibold text-pool-sofort'
                          : a.zielTag
                            ? 'text-ink2'
                            : 'text-muted'
                      }`}
                    >
                      {a.zielTag ? tagKompakt(a.zielTag) : 'ohne Datum'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          ))}
        </table>
      </div>
    </>
  )
}

/**
 * Ein Pfeil zum Blättern. `tun === null` heißt: dort ist nichts – der Knopf bleibt stehen,
 * abgeblendet. Ein Pfeil, der verschwindet, lässt einen suchen.
 */
function Blaetter({ richtung, tun }: { richtung: 'zurueck' | 'vor'; tun: (() => void) | null }) {
  return (
    <button
      type="button"
      onClick={() => tun?.()}
      disabled={tun === null}
      aria-label={richtung === 'zurueck' ? 'Eine Woche zurück' : 'Eine Woche vor'}
      className={`rounded-md px-1.5 py-0.5 text-[12px] ${
        tun === null ? 'cursor-default text-line2' : 'cursor-pointer text-ink2 hover:bg-[#f3f2ef]'
      }`}
    >
      {richtung === 'zurueck' ? '‹' : '›'}
    </button>
  )
}

function Legende({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1">{children}</span>
}

/**
 * Eine Zeile im Wochenraster – ein laufender Balken oder eine wartende Marke.
 *
 * **Position in Prozent, nicht in Rasterspalten.** Ein Balken beginnt an einem Tag und endet an
 * einem anderen; als `grid-column` müsste er auf ganze Spalten einrasten, und „läuft seit
 * Dienstag" sähe aus wie „läuft ab Mittwoch".
 *
 * ## Zwei Formen, ein Bauteil
 *
 * | | |
 * |---|---|
 * | **läuft** | voller Balken vom Anfang bis heute, gestrichelt weiter bis zum Zieltag |
 * | **wartet** | gestrichelte Marke auf **einem** Tag – dem, an dem sie dran ist |
 *
 * Eine wartende Aufgabe bekommt keinen Balken über mehrere Tage: niemand hat gesagt, wie lange
 * sie dauert. Ein Tag breit heißt „an diesem Tag fällig", und mehr ist nicht bekannt.
 */
function BalkenZeile({
  aufgabe,
  tage,
  heute,
  anMark,
  onKlick,
}: {
  aufgabe: Aufgabe
  tage: string[]
  heute: string
  anMark: (inhalt: React.ReactNode) => Record<string, unknown>
  onKlick: () => void
}) {
  const ersterTag = tage[0] as string
  const letzterTag = tage[tage.length - 1] as string
  const breite = 1 / tage.length
  const anteil = (tag: string) => {
    const i = tage.indexOf(tag)
    return i < 0 ? 0 : i * breite
  }

  const farbe = QUELLE_FARBE[aufgabe.quelle] ?? 'var(--color-ink2)'
  const gestrichelt = `repeating-linear-gradient(90deg, ${farbe} 0 3px, transparent 3px 6px)`

  const blase = (
    <>
      <b>{aufgabe.titel}</b>
      <br />
      {/**
       * **„mit" steht vorn, nicht hinten** ([E-282](../../../../../docs/ENTSCHEIDUNGEN.md#e-282)):
       * Es ändert die Aussage der ganzen Zeile – die Aufgabe liegt hier, gehört aber jemand
       * anderem. Wer das erst nach dem Termin liest, hat die Zeile schon falsch verstanden.
       */}
      {aufgabe.mit && (
        <>
          <i>mit – führend ist jemand anders</i>
          <br />
        </>
      )}
      {TASK_SOURCE_LABELS[aufgabe.quelle as TaskSource] ?? aufgabe.quelle} ·{' '}
      {aufgabe.art === 'laeuft' ? 'läuft' : 'wartet'}
      {aufgabe.startTag && <> · seit {tagKompakt(aufgabe.startTag)}</>}
      {aufgabe.zielTag && (
        <>
          {' '}
          · dran am {tagKompakt(aufgabe.zielTag)}
          {aufgabe.zielQuelle && ` (${aufgabe.zielQuelle})`}
        </>
      )}
    </>
  )

  /* ── Wartend: eine Marke auf ihrem Tag ──────────────────────────────── */
  if (aufgabe.art === 'wartet') {
    if (aufgabe.zielTag === null || !tage.includes(aufgabe.zielTag)) return null
    return (
      <button
        type="button"
        onClick={onKlick}
        {...anMark(blase)}
        className="relative block h-[9px] w-full cursor-pointer"
        style={aufgabe.mit ? { opacity: MIT_DECKKRAFT } : undefined}
        aria-label={`${aufgabe.titel}${aufgabe.mit ? ', als Mit-Bearbeiter' : ''}, wartet, dran am ${tagKompakt(
          aufgabe.zielTag,
        )}`}
      >
        <span
          className="absolute inset-y-[1px] rounded-[3px] border border-dashed"
          style={{
            left: `${anteil(aufgabe.zielTag) * 100}%`,
            width: `${breite * 100}%`,
            borderColor: farbe,
            background: gestrichelt,
            opacity: 0.75,
          }}
        />
      </button>
    )
  }

  /* ── Laufend: Balken vom Anfang bis heute, ggf. gestrichelt weiter ──── */
  const laeuftLaenger = (aufgabe.startTag as string) < ersterTag
  const von = laeuftLaenger ? 0 : anteil(aufgabe.startTag as string)

  /**
   * **Ein Balken zeigt gearbeitete Zeit – und die kann nicht in der Zukunft liegen.**
   *
   * Bis hierher endete er bei „heute oder Wochenende, je nachdem was früher kommt", und in einer
   * **künftigen** Woche hieß das: volle Breite. Nils' Bild von KW 35 zeigte deshalb fünfzehn
   * randvolle Balken – jeder eine Behauptung darüber, dass nächste Woche noch daran gearbeitet
   * wird. Das weiß niemand.
   *
   * Jetzt endet der feste Teil beim heutigen Tag. Liegt die ganze Woche danach, ist er **null
   * breit** und wird nicht gezeichnet; was dort läuft, steht als Zahl neben dem Namen.
   */
  const heuteEnde = heute < ersterTag ? 0 : tage.includes(heute) ? anteil(heute) + breite : 1
  const fest = heuteEnde <= von ? 0 : Math.max(heuteEnde - von, breite / 2)

  const planUeberWoche = aufgabe.zielTag !== null && aufgabe.zielTag > letzterTag
  const planEnde =
    aufgabe.zielTag === null ? null : planUeberWoche ? 1 : anteil(aufgabe.zielTag) + breite
  const plan = planEnde === null ? 0 : Math.max(planEnde - (von + fest), 0)

  /** Nichts Gearbeitetes und nichts Geplantes in dieser Woche: dann gehört hier keine Zeile hin. */
  if (fest === 0 && plan === 0) return null

  return (
    <button
      type="button"
      onClick={onKlick}
      {...anMark(blase)}
      className="relative block h-[9px] w-full cursor-pointer"
      style={aufgabe.mit ? { opacity: MIT_DECKKRAFT } : undefined}
      aria-label={`${aufgabe.titel}${aufgabe.mit ? ', als Mit-Bearbeiter' : ''}, läuft seit ${tagKompakt(
        aufgabe.startTag as string,
      )}${aufgabe.zielTag ? `, geplant bis ${tagKompakt(aufgabe.zielTag)}` : ''}`}
    >
      {fest > 0 && (
        <span
          className="absolute inset-y-0 rounded-[4px]"
          style={{
            left: `${von * 100}%`,
            width: `${fest * 100}%`,
            background: farbe,
            /** Links offen, wenn die Aufgabe schon vorher lief – ein runder Anfang wäre gelogen. */
            borderTopLeftRadius: laeuftLaenger ? 0 : undefined,
            borderBottomLeftRadius: laeuftLaenger ? 0 : undefined,
          }}
        />
      )}
      {plan > 0 && (
        <span
          className="absolute inset-y-[2px] rounded-[3px]"
          style={{
            left: `${(von + fest) * 100}%`,
            width: `${plan * 100}%`,
            background: gestrichelt,
          }}
        />
      )}
      {laeuftLaenger && fest > 0 && (
        <span className="absolute -left-2 top-[-3px] text-[9px] text-ink2" aria-hidden>
          ◀
        </span>
      )}
      {planUeberWoche && (
        <span className="absolute -right-2 top-[-3px] text-[9px] text-ink2" aria-hidden>
          ▸
        </span>
      )}
    </button>
  )
}
